/**
 * AVOD plugin — consumes platform ad cues; does not decide monetization.
 *
 * Sprint 8 Phase C foundation:
 * - Emits PlaybackPlatformEvent AD_BREAK_* on the bus
 * - Optional pre-roll: briefly loads ad URL then restores content source
 *
 * Full mid-roll timeline scheduling is Version 2 / SSAI territory.
 */

import { createPlugin } from "../createPlugin";
import { AdBreakKind } from "../../Core/EntitlementModel";
import { PlaybackPlatformEvent } from "../../Core/PlaybackPlatformEvents";
import { PlayerEvent } from "../../Core/PlayerEvents";

var PRE = 1;
var POST = 2;
var BETWEEN = 3;

/**
 * Normalize videos_ads / AssignVideoAd rows into cues.
 * @param {object[]} rows
 * @returns {object[]}
 */
export function normalizeAdCues(rows) {
  if (!Array.isArray(rows)) return [];
  return rows
    .map(function (row) {
      var adType = Number(row.ad_type);
      var kind = AdBreakKind.MID_ROLL;
      if (adType === PRE) kind = AdBreakKind.PRE_ROLL;
      else if (adType === POST) kind = AdBreakKind.POST_ROLL;
      else if (adType === BETWEEN) kind = AdBreakKind.MID_ROLL;
      return {
        id: row.id != null ? String(row.id) : null,
        kind: kind,
        adType: adType,
        url: row.ad_url || row.url || null,
        timeOffset:
          row.time != null
            ? Number(row.time)
            : row.video_time != null
              ? Number(row.video_time)
              : null,
      };
    })
    .filter(function (c) {
      return !!c.url;
    });
}

/**
 * @param {object} [options]
 * @param {object[]} [options.cues]
 * @param {boolean} [options.enablePreRoll=true]
 * @param {string} [options.provider=internal] internal | external
 * @param {string} [options.vastTagUrl] external VAST tag (stub — player VAST client deferred)
 * @param {string} [options.vmapTagUrl] external VMAP tag (stub)
 * @returns {object}
 */
export function createAvodPlugin(options) {
  options = options || {};
  var cues = Array.isArray(options.cues) ? options.cues.slice() : [];
  var enablePreRoll = options.enablePreRoll !== false;
  var provider = options.provider || "internal";
  var vastTagUrl = options.vastTagUrl || "";
  var vmapTagUrl = options.vmapTagUrl || "";
  var playedPre = false;
  var contentSource = null;
  var bus = null;
  var engine = null;

  // External VAST/VMAP: architecture-ready. Full IMA/VAST parsing deferred;
  // when provider is external and a tag URL exists, expose it on the plugin
  // for host players (JW VAST path) without breaking internal cue serving.
  if (provider === "external" && (vastTagUrl || vmapTagUrl) && cues.length === 0) {
    cues = [
      {
        id: "external-vast",
        kind: AdBreakKind.PRE_ROLL,
        adType: PRE,
        url: vastTagUrl || vmapTagUrl,
        timeOffset: null,
        vast: true,
      },
    ];
  }

  function publish(name, payload) {
    if (bus && bus.publish) bus.publish(name, payload || {});
  }

  function preRolls() {
    return cues.filter(function (c) {
      return c.kind === AdBreakKind.PRE_ROLL;
    });
  }

  function postRolls() {
    return cues.filter(function (c) {
      return c.kind === AdBreakKind.POST_ROLL;
    });
  }

  return createPlugin({
    name: "avod",
    version: "0.1.0",
    onRegister: function (ctx) {
      engine = ctx.engine;
      bus = ctx.bus || (engine && engine.getEventBus && engine.getEventBus());
      if (ctx.config && ctx.config.source) {
        contentSource =
          typeof ctx.config.source === "string"
            ? ctx.config.source
            : ctx.config.source.src;
      }
    },
    onReady: function (ctx) {
      engine = ctx.engine || engine;
      bus = ctx.bus || bus;
      if (!enablePreRoll || playedPre) return;
      var pre = preRolls();
      if (!pre.length || !engine || !engine.load) return;
      playedPre = true;
      var ad = pre[0];
          publish(PlaybackPlatformEvent.AD_BREAK_START, {
        kind: ad.kind,
        url: ad.url,
      });
      publish("AdBreakStarted", { kind: ad.kind, url: ad.url });
      var restore = contentSource;
      engine
        .load({ src: ad.url, type: "mp4" })
        .then(function () {
          return engine.play && engine.play();
        })
        .catch(function () {
          publish("AdBreakError", { kind: ad.kind });
          if (restore) {
            engine.load({ src: restore }).then(function () {
              if (engine.play) engine.play();
            });
          }
        });

      var unsub = engine.on && engine.on(PlayerEvent.PLAYBACK_ENDED, function () {
        if (unsub) unsub();
        publish(PlaybackPlatformEvent.AD_BREAK_END, { kind: ad.kind });
        publish("AdBreakEnded", { kind: ad.kind });
        if (restore) {
          engine.load({ src: restore }).then(function () {
            if (engine.play) engine.play();
          });
        }
      });
    },
    onEvent: function (eventName) {
      if (
        eventName === PlayerEvent.PLAYBACK_ENDED ||
        eventName === "PlaybackEnded"
      ) {
        var post = postRolls();
        if (post.length) {
          publish("AdBreakStarted", {
            kind: AdBreakKind.POST_ROLL,
            url: post[0].url,
          });
        }
      }
    },
    onDestroy: function () {
      engine = null;
      bus = null;
    },
  });
}

export default createAvodPlugin;
